import React from 'react';
import type { Language } from '@nordcrest/shared';
import { t } from '../../i18n';
import { Button, Card, CardContent } from '../ui';

interface CompletionScreenProps {
  language: Language;
  wantsCallback: boolean;
  onReset: () => void;
}

export function CompletionScreen({ language, wantsCallback, onReset }: CompletionScreenProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-primary-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <Card>
          <CardContent className="p-8 text-center">
            {/* Success icon */}
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('completion.title', language)}
            </h2>

            <p className="text-gray-600 mb-6">
              {t('completion.message', language)}
            </p>

            {/* Email reminder */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-blue-600 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-blue-800 font-medium">
                  {t('completion.checkEmail', language)}
                </span>
              </div>
            </div>

            {/* Callback confirmation */}
            {wantsCallback && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-green-600 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span className="text-green-800 font-medium">
                    {t('completion.callback', language)}
                  </span>
                </div>
              </div>
            )}

            {/* What to expect */}
            <div className="text-left bg-gray-50 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                {language === 'sv' ? 'Vad händer nu?' : 'What happens next?'}
              </h3>
              <ul className="text-sm text-gray-600 space-y-2">
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  {language === 'sv'
                    ? 'Din rapport genereras nu med AI-analys'
                    : 'Your report is being generated with AI analysis'}
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  {language === 'sv'
                    ? 'Du får ett mail inom 5 minuter'
                    : 'You will receive an email within 5 minutes'}
                </li>
                <li className="flex items-start">
                  <span className="text-primary-600 mr-2">•</span>
                  {language === 'sv'
                    ? 'Rapporten innehåller personliga rekommendationer'
                    : 'The report contains personalized recommendations'}
                </li>
              </ul>
            </div>

            <Button variant="secondary" onClick={onReset}>
              {t('completion.newSurvey', language)}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-sm text-gray-400 mt-6">
          {language === 'sv'
            ? 'Tack för ditt intresse! Vi ser fram emot att hjälpa er AI-resa.'
            : 'Thank you for your interest! We look forward to helping your AI journey.'}
        </p>
      </div>
    </div>
  );
}
